import { SEGMENT_NAME_MAX, type AdHocFilter, type SegmentDetail, type SegmentPerson, type SegmentRule } from "@domains/segments/shared/segmentsTypes.ts";
import { isPureReportingManager } from "@packages/core/session/roles.ts";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { FieldError } from "@/components/ui/field.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Menu, MenuContent, MenuItem, MenuLabel } from "@/components/ui/menu.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { TextField } from "@/components/ui/text-field.tsx";
import { TrashIcon } from "@heroicons/react/20/solid";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { useCreateSegmentMutationOptions, useRemoveSegmentMutationOptions, useSegmentsClient, useUpdateSegmentMutationOptions } from "@domains/segments/client/api/segmentsQuery.ts";
import { validateSegmentName } from "@domains/segments/client/components/validateSegmentName.ts";
import { segmentsI18NextInstanceKey } from "@domains/segments/client/i18n/i18next.ts";
import { SegmentMembers } from "@domains/segments/client/components/members/SegmentMembers.tsx";
import { SegmentMemberPreview } from "@domains/segments/client/components/preview/SegmentMemberPreview.tsx";
import { SegmentRules } from "@domains/segments/client/components/rules/SegmentRules.tsx";
import { SegmentModeChooser } from "@domains/segments/client/components/SegmentModeChooser.tsx";
import { SegmentTags } from "@domains/segments/client/components/tags/SegmentTags.tsx";
import { ConfirmDialog } from "@packages/core-client/forms/ConfirmDialog.tsx";
import { useSkeletonState } from "@packages/core-client/hooks/useSkeletonState.ts";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { ErrorState } from "@packages/core-client/errors/ErrorState.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { forwardRef, useCallback, useImperativeHandle, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { setTeamAdHocFilter } from "../../hooks/teamAdHocFilter.ts";
import { useTeamSegment } from "../../hooks/useTeamSegment.ts";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";

const PLACEHOLDER_FIELDS = { items: [] };

export interface TeamSegmentFormProps {
    segment?: SegmentDetail;
    onDone?: () => void;
    onCreated?: (segmentId: string) => void;
    onApply?: () => void;
    onFilterApplied?: () => void;
}

export interface TeamSegmentFormHandle {
    requestLeave: (proceed: () => void) => void;
}

export const TeamSegmentForm = forwardRef<TeamSegmentFormHandle, TeamSegmentFormProps>(function TeamSegmentForm({ segment, onDone, onCreated, onApply, onFilterApplied }, ref) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t: tSegmentForm } = useTranslation("SegmentForm", { i18n: i18nextInstance });
    const teamI18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("TeamSegmentForm", { i18n: teamI18nextInstance });
    const session = useSession();
    const { queryUtils } = useSegmentsClient();
    const [, setSegmentId] = useTeamSegment();

    const createMutation = useMutation(useCreateSegmentMutationOptions());
    const updateMutation = useMutation(useUpdateSegmentMutationOptions());
    const removeMutation = useMutation(useRemoveSegmentMutationOptions());

    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const [rulesResetCount, setRulesResetCount] = useState(0);
    const [pendingMode, setPendingMode] = useState<boolean | undefined>(undefined);

    const {
        data: employeeFields,
        isPending: areFieldsPending,
        isError: haveFieldsFailed,
        refetch: refetchFields
    } = useQuery(
        createQueryOptions(queryUtils.listEmployeeFields.queryOptions({ input: { orgId: session?.organizationId ?? "" } }), {
            throwOnError: false
        })
    );

    const { isLoading: areFieldsLoading, hasContent: hasFieldsContent } = useSkeletonState({ isPending: areFieldsPending, data: employeeFields ? [employeeFields] : undefined, placeholder: [PLACEHOLDER_FIELDS] });

    const handleRetryFields = useCallback(() => void refetchFields(), [refetchFields]);

    const isReadOnly = !!segment && !segment.isEditable;
    const isSaving = createMutation.isPending || updateMutation.isPending;

    const validateName = useCallback((name: string) => validateSegmentName(name, { required: tSegmentForm("name.required"), tooLong: tSegmentForm("name.tooLong", { count: SEGMENT_NAME_MAX }) }), [tSegmentForm]);

    const form = useForm({
        defaultValues: { name: segment?.name ?? "", isRuleBased: segment?.isRuleBased ?? true, matchType: segment?.matchType ?? "all", rules: segment?.rules ?? [], hasIncompleteRule: false, members: segment?.members ?? [], tags: segment?.tags ?? [] },
        onSubmit: async ({ value, formApi }) => {
            const name = value.name.trim();
            const definition = { name, isRuleBased: value.isRuleBased, matchType: value.matchType, tags: value.tags, ...(value.isRuleBased ? { rules: value.rules } : { personIds: value.members.map(member => member.id) }) };
            const baseline = { name, isRuleBased: value.isRuleBased, matchType: value.matchType, rules: value.rules, hasIncompleteRule: false, members: value.members, tags: value.tags };

            try {
                if (segment) {
                    await updateMutation.mutateAsync({ segmentId: segment.id, ...definition });
                    formApi.reset(baseline);

                    return;
                }

                const created = await createMutation.mutateAsync(definition);
                formApi.reset(baseline);
                onCreated?.(created.id);
            } catch {
                toast.error(tSegmentForm("saveErrorToast"), { position: "top-right" });
            }
        }
    });

    const [pendingLeave, setPendingLeave] = useState<(() => void) | undefined>(undefined);

    useImperativeHandle(
        ref,
        () => ({
            requestLeave: proceed => (isReadOnly || form.state.isDefaultValue ? proceed() : setPendingLeave(() => proceed))
        }),
        [form, isReadOnly]
    );

    const handleLeave = useCallback(() => {
        pendingLeave?.();
        setPendingLeave(undefined);
    }, [pendingLeave]);
    const handleStay = useCallback(() => setPendingLeave(undefined), []);

    const handleSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();
            event.stopPropagation();
            setHasAttemptedSave(true);

            if (form.state.values.isRuleBased && form.state.values.hasIncompleteRule) {
                return;
            }

            if (!form.state.values.isRuleBased && form.state.values.members.length === 0) {
                return;
            }

            void form.handleSubmit();
        },
        [form]
    );
    const handleDiscard = useCallback(() => {
        form.reset();
        setRulesResetCount(count => count + 1);
        setHasAttemptedSave(false);
    }, [form]);

    const handleApplyDraft = useCallback(() => {
        setHasAttemptedSave(true);

        if (form.state.values.hasIncompleteRule || form.state.values.rules.length === 0) {
            return;
        }

        const filter: AdHocFilter = { matchType: form.state.values.matchType, rules: form.state.values.rules };
        setTeamAdHocFilter({ filter, draft: filter });
        onFilterApplied?.();
    }, [form, onFilterApplied]);

    const handleSaveAsNew = useCallback(async () => {
        setHasAttemptedSave(true);

        if (form.state.values.hasIncompleteRule || form.state.values.rules.length === 0) {
            return;
        }

        const name = t("copyName", { name: form.state.values.name.trim() }).slice(0, SEGMENT_NAME_MAX);

        try {
            const created = await createMutation.mutateAsync({ name, isRuleBased: true, matchType: form.state.values.matchType, rules: form.state.values.rules, tags: form.state.values.tags });
            toast.success(tSegmentForm("createdToast"), { position: "top-right" });
            void setSegmentId(created.id);
            onFilterApplied?.();
        } catch {
            toast.error(tSegmentForm("saveErrorToast"), { position: "top-right" });
        }
    }, [form, t, createMutation, tSegmentForm, setSegmentId, onFilterApplied]);
    const handleSaveAsNewPress = useCallback(() => void handleSaveAsNew(), [handleSaveAsNew]);

    const handleRulesChange = useCallback((rules: SegmentRule[]) => form.setFieldValue("rules", rules), [form]);
    const handleValidityChange = useCallback((hasIncompleteRule: boolean) => form.setFieldValue("hasIncompleteRule", hasIncompleteRule), [form]);
    const handleMembersChange = useCallback((members: SegmentPerson[]) => form.setFieldValue("members", members), [form]);
    const handleTagsChange = useCallback((tags: string[]) => form.setFieldValue("tags", tags), [form]);

    const handleModeChange = useCallback(
        (isRuleBased: boolean) => {
            if (!isRuleBased || form.state.values.members.length === 0) {
                form.setFieldValue("hasIncompleteRule", false);
                form.setFieldValue("isRuleBased", isRuleBased);

                return;
            }

            setPendingMode(isRuleBased);
        },
        [form]
    );

    const handleCancelModeChange = useCallback(() => setPendingMode(undefined), []);
    const handleConfirmModeChange = useCallback(() => {
        if (pendingMode === undefined) {
            return;
        }

        form.setFieldValue("hasIncompleteRule", false);
        form.setFieldValue("isRuleBased", pendingMode);
        form.setFieldValue("members", []);
        setPendingMode(undefined);
    }, [form, pendingMode]);

    const handleRequestDelete = useCallback(() => setIsConfirmingDelete(true), []);
    const handleCancelDelete = useCallback(() => setIsConfirmingDelete(false), []);
    const handleConfirmDelete = useCallback(async () => {
        if (!segment) {
            return;
        }

        try {
            await removeMutation.mutateAsync({ segmentId: segment.id });
            form.reset();
            onDone?.();
        } catch {
            setIsConfirmingDelete(false);
            toast.error(tSegmentForm("deleteErrorToast"), { position: "top-right" });
        }
    }, [form, onDone, removeMutation, segment, tSegmentForm]);
    const handleConfirmDeletePress = useCallback(() => void handleConfirmDelete(), [handleConfirmDelete]);

    if (isConfirmingDelete && segment) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
                <p className="text-fg font-semibold">{t("deleteConfirm.title", { name: segment.name })}</p>
                <p className="text-muted-fg max-w-sm text-sm">{t("deleteConfirm.description")}</p>
                <div className="flex gap-2">
                    <Button intent="outline" onPress={handleCancelDelete} isDisabled={removeMutation.isPending} name="cancel-delete">
                        {t("deleteConfirm.cancel")}
                    </Button>
                    <Button intent="danger" onPress={handleConfirmDeletePress} isPending={removeMutation.isPending} name="delete-confirm">
                        {t("deleteConfirm.confirm")}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <form noValidate className="flex h-full flex-col" onSubmit={handleSubmit}>
            <div className="bg-overlay sticky top-0 z-10 px-6 pt-6 pb-5">
                <div className="flex min-w-0 items-center gap-2">
                    <p className="text-fg truncate text-base font-semibold">{isReadOnly && segment ? segment.name : segment ? tSegmentForm("editTitle") : tSegmentForm("createTitle")}</p>
                    {isReadOnly && <Badge intent="secondary">{t("sharedPill")}</Badge>}
                </div>
            </div>

            <div className="flex flex-col gap-5 px-6 pb-6">
                {!isReadOnly && (
                    <form.Field name="name" validators={{ onChange: ({ value }) => validateName(value), onSubmit: ({ value }) => validateName(value) }}>
                        {field => (
                            <TextField aria-label={tSegmentForm("name.label")} value={field.state.value} onChange={field.handleChange} isInvalid={field.state.meta.errors.length > 0}>
                                <Input name="name" onBlur={field.handleBlur} placeholder={tSegmentForm("name.placeholder")} />
                                <FieldError>{field.state.meta.errors[0]}</FieldError>
                            </TextField>
                        )}
                    </form.Field>
                )}

                <form.Field name="isRuleBased">{field => <SegmentModeChooser isRuleBased={field.state.value} isReadOnly={isReadOnly} onChange={handleModeChange} />}</form.Field>

                <form.Subscribe selector={state => ({ isRuleBased: state.values.isRuleBased, matchType: state.values.matchType, rules: state.values.rules })}>
                    {({ isRuleBased, matchType, rules }) =>
                        isRuleBased ? (
                            <>
                                <output className="sr-only">{areFieldsLoading && tSegmentForm("rules.loading")}</output>

                                {haveFieldsFailed ? (
                                    <ErrorState isEmbedded kind="load" title={tSegmentForm("rules.error")} onRetry={handleRetryFields} retryLabel={tSegmentForm("rules.retry")} />
                                ) : !hasFieldsContent ? null : areFieldsLoading || !employeeFields ? (
                                    <Skeleton isLoading={areFieldsLoading} inert={areFieldsLoading} className="flex flex-col gap-2">
                                        <div className="flex flex-col gap-3">
                                            <div className="flex items-center gap-2">
                                                <div className="h-4 w-12" />
                                                <div className="h-9 w-24" />
                                                <div className="h-4 w-28" />
                                            </div>
                                            <div className="flex flex-col gap-2 sm:flex-row">
                                                <div className="h-9 w-full sm:w-44" />
                                                <div className="h-9 w-full sm:w-40" />
                                                <div className="h-9 w-full sm:flex-1" />
                                            </div>
                                        </div>
                                    </Skeleton>
                                ) : (
                                    <SegmentRules
                                        key={rulesResetCount}
                                        fields={employeeFields.items}
                                        rules={rules}
                                        hasRequiredRules={segment ? segment.requiredRules.length > 0 : isPureReportingManager(session)}
                                        hasUnlistedRules={segment?.hasUnlistedRules ?? false}
                                        hasUnlistedRequiredRules={segment?.hasUnlistedRequiredRules ?? false}
                                        matchType={matchType}
                                        isMatchTypeFixed
                                        matchTypeFixedReason={t("matchTypeFixed")}
                                        isReadOnly={isReadOnly}
                                        isOwnedByViewer={!segment || segment.isEditable}
                                        ownerDisplayName={segment?.ownerDisplayName}
                                        shouldShowErrors={hasAttemptedSave}
                                        onRulesChange={handleRulesChange}
                                        onValidityChange={handleValidityChange}
                                    >
                                        {rules.length > 0 && <SegmentMemberPreview key={rulesResetCount} matchType={matchType} rules={rules} />}
                                    </SegmentRules>
                                )}
                            </>
                        ) : null
                    }
                </form.Subscribe>

                <form.Subscribe selector={state => state.values.isRuleBased}>
                    {isRuleBased => (isRuleBased ? null : <form.Field name="members">{field => <SegmentMembers members={field.state.value} isReadOnly={isReadOnly} shouldShowErrors={hasAttemptedSave} onChange={handleMembersChange} />}</form.Field>)}
                </form.Subscribe>

                <form.Field name="tags">{field => <SegmentTags tags={field.state.value} isReadOnly={isReadOnly} onChange={handleTagsChange} />}</form.Field>
            </div>

            {!isReadOnly && (
                <div className="bg-overlay sticky bottom-0 flex items-center justify-end gap-2 px-6 py-4">
                    {segment && (
                        <Menu>
                            <Button intent="plain" size="sq-sm" aria-label={t("moreActionsAriaLabel")} name="segment-actions">
                                <EllipsisHorizontalIcon aria-hidden="true" />
                            </Button>
                            <MenuContent placement="top start">
                                <MenuItem intent="danger" onAction={handleRequestDelete} textValue={tSegmentForm("delete")}>
                                    <TrashIcon aria-hidden="true" />
                                    <MenuLabel>{tSegmentForm("delete")}</MenuLabel>
                                </MenuItem>
                            </MenuContent>
                        </Menu>
                    )}
                    <form.Subscribe selector={state => ({ isModified: !state.isDefaultValue, isRuleBased: state.values.isRuleBased, hasIncompleteRule: state.values.hasIncompleteRule, hasNoRules: state.values.rules.length === 0 })}>
                        {({ isModified, isRuleBased, hasIncompleteRule, hasNoRules }) =>
                            isRuleBased && isModified ? (
                                segment ? (
                                    <>
                                        <Button type="button" intent="outline" onPress={handleApplyDraft} isDisabled={hasIncompleteRule || hasNoRules} name="apply">
                                            {t("apply")}
                                        </Button>
                                        <Button type="button" intent="outline" onPress={handleSaveAsNewPress} isPending={createMutation.isPending} isDisabled={hasIncompleteRule || hasNoRules} name="save-as-new">
                                            {t("saveAsNew")}
                                        </Button>
                                        <Button type="submit" intent="primary" isPending={isSaving} isDisabled={hasIncompleteRule} name="save-changes">
                                            {t("saveChanges")}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button type="submit" intent="outline" isPending={isSaving} isDisabled={hasIncompleteRule} name="save-as-segment">
                                            {t("saveAsSegment")}
                                        </Button>
                                        <Button type="button" intent="primary" onPress={handleApplyDraft} isDisabled={hasIncompleteRule || hasNoRules} name="apply">
                                            {t("apply")}
                                        </Button>
                                    </>
                                )
                            ) : (
                                <>
                                    {!isModified && onApply ? (
                                        <Button type="button" intent="outline" onPress={onApply} name="apply">
                                            {t("apply")}
                                        </Button>
                                    ) : (
                                        <Button type="button" intent="plain" onPress={handleDiscard} isDisabled={isSaving || !isModified} name="discard">
                                            {tSegmentForm("discard")}
                                        </Button>
                                    )}
                                    <Button type="submit" intent="primary" isPending={isSaving} isDisabled={!isModified} name="save">
                                        {tSegmentForm("save")}
                                    </Button>
                                </>
                            )
                        }
                    </form.Subscribe>
                </div>
            )}

            <ConfirmDialog
                isOpen={pendingLeave !== undefined}
                title={tSegmentForm("leaveConfirm.title")}
                description={tSegmentForm("leaveConfirm.description")}
                confirmLabel={tSegmentForm("leaveConfirm.leave")}
                cancelLabel={tSegmentForm("leaveConfirm.stay")}
                confirmIntent="danger"
                onConfirm={handleLeave}
                onCancel={handleStay}
                confirmName="leave"
            />

            <ConfirmDialog
                isOpen={pendingMode !== undefined}
                title={tSegmentForm("modeConfirm.title")}
                description={tSegmentForm("modeConfirm.description")}
                confirmLabel={tSegmentForm("modeConfirm.confirm")}
                cancelLabel={tSegmentForm("modeConfirm.cancel")}
                confirmIntent="danger"
                onConfirm={handleConfirmModeChange}
                onCancel={handleCancelModeChange}
                confirmName="mode-switch"
                cancelName="keep-list"
            />
        </form>
    );
});
